import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import { Box } from "@mui/material";
import { useState } from "react";

const LOGO_BASE = "/coding-platform/logos";

type LangMeta = {
  logo: string;
  bg: string;
  label: string;
  color: string;
  fit?: "contain" | "cover";
  pad?: number;
};

const LANG_META: Record<string, LangMeta> = {
  python: { logo: `${LOGO_BASE}/python.svg`, bg: "#ffffff", label: "Py", color: "#3776AB", fit: "contain", pad: 0.45 },
  javascript: { logo: `${LOGO_BASE}/javascript.svg`, bg: "#F7DF1E", label: "JS", color: "#323330", fit: "cover", pad: 0 },
  typescript: { logo: `${LOGO_BASE}/typescript.svg`, bg: "#3178C6", label: "TS", color: "#ffffff", fit: "cover", pad: 0 },
  java: { logo: `${LOGO_BASE}/java.svg`, bg: "#ffffff", label: "Jv", color: "#E76F00", fit: "contain", pad: 0.35 },
  c: { logo: `${LOGO_BASE}/c.svg?v=10`, bg: "#ffffff", label: "C", color: "#5C6BC0", fit: "contain", pad: 0.35 },
  csharp: { logo: `${LOGO_BASE}/csharp.svg?v=6`, bg: "#ffffff", label: "C#", color: "#68217A", fit: "contain", pad: 0.35 },
  cpp: { logo: `${LOGO_BASE}/cpp.svg`, bg: "#ffffff", label: "C++", color: "#00599C", fit: "contain", pad: 0.35 },
  "c++": { logo: `${LOGO_BASE}/cpp.svg`, bg: "#ffffff", label: "C++", color: "#00599C", fit: "contain", pad: 0.35 },
  react: { logo: `${LOGO_BASE}/react.svg`, bg: "#282c34", label: "Re", color: "#61DAFB", fit: "contain", pad: 0.4 },
  go: { logo: `${LOGO_BASE}/go.svg`, bg: "#ffffff", label: "Go", color: "#00ADD8", fit: "contain", pad: 0.3 },
  golang: { logo: `${LOGO_BASE}/go.svg`, bg: "#ffffff", label: "Go", color: "#00ADD8", fit: "contain", pad: 0.3 },
  "node.js": { logo: `${LOGO_BASE}/javascript.svg`, bg: "#F7DF1E", label: "JS", color: "#323330", fit: "cover", pad: 0 },
  node: { logo: `${LOGO_BASE}/javascript.svg`, bg: "#F7DF1E", label: "JS", color: "#323330", fit: "cover", pad: 0 },
  rust: { logo: `${LOGO_BASE}/rust.svg`, bg: "#ffffff", label: "Rs", color: "#DEA584", fit: "contain", pad: 0.35 },
  kotlin: { logo: `${LOGO_BASE}/kotlin.svg`, bg: "#ffffff", label: "Kt", color: "#7F52FF", fit: "contain", pad: 0.35 },
  swift: { logo: `${LOGO_BASE}/swift.svg`, bg: "#ffffff", label: "Sw", color: "#F05138", fit: "contain", pad: 0.35 },
  sql: { logo: `${LOGO_BASE}/sql.svg`, bg: "#ffffff", label: "SQL", color: "#4479A1", fit: "contain", pad: 0.35 },
  dsa: { logo: "", bg: "#ecfdf5", label: "DSA", color: "#047857" },
  "interview prep": { logo: "", bg: "#fce7f3", label: "IP", color: "#be185d" },
};

const ALIASES: Record<string, string> = {
  py: "python",
  js: "javascript",
  ts: "typescript",
  jv: "java",
  "c#": "csharp",
  cs: "csharp",
  "c sharp": "csharp",
  "c-sharp": "csharp",
};

export function resolveCourseLangKey(language?: string, category?: string): string {
  const raw = String(language || category || "").toLowerCase().trim();
  if (ALIASES[raw]) return ALIASES[raw];
  if (LANG_META[raw]) return raw;
  const partial = Object.keys(LANG_META).find((key) => raw.includes(key));
  return partial ?? "default";
}

function getLangMeta(language?: string, category?: string): LangMeta {
  const key = resolveCourseLangKey(language, category);
  if (key !== "default" && LANG_META[key]) return LANG_META[key];
  const label = (language || category || "CP").replace(/[^a-zA-Z+]/g, "").slice(0, 3).toUpperCase() || "CP";
  return { bg: "#eff6ff", label, color: "#1d4ed8", logo: "", fit: "contain", pad: 0 };
}

type Props = { language?: string; category?: string; size?: number };

export default function CourseLangLogo({ language, category, size = 44 }: Props) {
  const meta = getLangMeta(language, category);
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = !!meta.logo && !imgFailed;
  const pad = meta.pad ?? 0.4;
  const fit = meta.fit ?? "contain";

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "10px",
        bgcolor: meta.bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 2px 10px rgba(15,23,42,0.1)",
        border: "1px solid rgba(15,23,42,0.06)",
        flexShrink: 0,
        overflow: "hidden",
        p: showImage ? pad : 0,
      }}
    >
      {showImage ? (
        <Box
          component="img"
          src={meta.logo}
          alt={`${language || category || "Course"} logo`}
          onError={() => setImgFailed(true)}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: fit,
            objectPosition: "center",
            display: "block",
          }}
        />
      ) : meta.label === "CP" ? (
        <CodeOutlinedIcon sx={{ fontSize: 22, color: meta.color }} />
      ) : (
        <Box
          sx={{
            fontWeight: 800,
            fontSize: meta.label.length > 3 ? 10 : meta.label.length > 2 ? 11 : 14,
            color: meta.color,
            lineHeight: 1,
          }}
        >
          {meta.label}
        </Box>
      )}
    </Box>
  );
}
